import { useState, createContext, useContext } from "react"
import { node, func } from "prop-types"
import { colors } from "components/shared/admin_colors"
import { Pane, StatusMessage, Loading } from "components/ui"
import { Button, StackView } from "@planningcenter/tapestry-react"

const SettingContext = createContext()

const SettingContextProvider = ({ children }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState(null)

  const handleSuccess = () => {
    setIsSuccess(true)
    setTimeout(() => setIsSuccess(false), 3000)
    setIsSaving(false)
    setIsEditing(false)
    setError(null)
  }

  const handleError = error => {
    setError(error)
    setIsSaving(false)
  }

  const handleSaving = () => {
    setError(null)
    setIsSaving(true)
  }

  const handleCancel = () => {
    setError(null)
    setIsEditing(false)
  }

  return (
    <SettingContext.Provider
      value={{
        isEditing,
        isSaving,
        isSuccess,
        isLoading,
        error,
        onEditing: setIsEditing,
        onSaving: handleSaving,
        onLoading: setIsLoading,
        onSuccess: handleSuccess,
        onError: handleError,
        onCancel: handleCancel
      }}
    >
      {children}
    </SettingContext.Provider>
  )
}

SettingContextProvider.propTypes = {
  children: node
}

const useSettings = (initialValues = {}) => {
  const context = useContext(SettingContext)

  if (context === undefined) {
    throw new Error("SettingPane and useSetting must be used within a SettingContextProvider")
  }

  return { ...context, ...initialValues }
}

const SettingPane = ({ title, blankState = null, children, onSave, onCancel }) => {
  const { isEditing, isSaving, isLoading, isSuccess, error, onEditing } = useSettings()

  let paneStatus = null

  if (isSaving) paneStatus = { type: "saving", text: "Saving...", icon: "spinner" }
  if (isSuccess) paneStatus = { type: "success", text: "Saved!", icon: "general#check" }
  if (error)
    paneStatus = { type: "error", text: "Changes not saved.", icon: "general#exclamation-triangle" }

  return (
    <Pane className="pane mb-4">
      <Pane.Header axis="vertical" alignment="flex-start" distribution="center">
        <StackView
          axis="horizontal"
          alignment="center"
          distribution="space-between"
          spacing={1}
          width="100%"
        >
          <Pane.Title>{title}</Pane.Title>
          <StackView axis="horizontal" alignment="center" spacing={1}>
            {paneStatus && <StatusMessage {...paneStatus} />}
            {!isLoading && !isEditing && !blankState && (
              <Button onClick={() => onEditing(true)} variant="outline" size="xs" title="Edit" />
            )}

            {!isLoading && isEditing && (
              <StackView axis="horizontal" spacing={1}>
                <Button
                  disabled={paneStatus === "saving"}
                  onClick={onCancel}
                  size="xs"
                  title="Cancel"
                />
                <Button
                  disabled={paneStatus === "saving"}
                  onClick={onSave}
                  size="xs"
                  theme="create"
                  title="Save"
                />
              </StackView>
            )}
          </StackView>
        </StackView>
      </Pane.Header>

      {isLoading && (
        <Pane.Body>
          <Loading />
        </Pane.Body>
      )}

      {!isLoading && blankState && <Pane.BlankState margin={2}>{blankState}</Pane.BlankState>}

      {!isLoading && !blankState && (
        <Pane.Body>
          {!isSaving && error && <div style={{ color: colors.error }}>{error}</div>}
          {children}
        </Pane.Body>
      )}
    </Pane>
  )
}

SettingPane.propTypes = {
  title: node,
  blankState: node,
  children: node,
  onSave: func,
  onCancel: func
}

export { useSettings, SettingPane, SettingContextProvider, SettingContext }
